import React, { useState, useEffect } from 'react';
import { Card, Select, Spin, message, Statistic, Row, Col, Table, Tag, Button } from 'antd';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import { FullscreenOutlined, FullscreenExitOutlined } from '@ant-design/icons';
import api from '../services/api';
import 'leaflet/dist/leaflet.css';

const { Option } = Select;

const CoverageMap = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('active'); // 'active' ou 'all'
  const [viewMode, setViewMode] = useState('state'); // 'state' ou 'city'
  const [selectedState, setSelectedState] = useState(null); // Estado selecionado
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/coverage-map', {
        params: { filter }
      });
      setData(response.data);
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
      message.error('Erro ao carregar dados do mapa');
    } finally {
      setLoading(false);
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!data) return null;

  // Filtrar times por estado selecionado
  const filteredTeams = selectedState 
    ? data.teams.filter(t => t.state === selectedState)
    : data.teams;

  // Preparar marcadores baseado no modo de visualização
  const markers = viewMode === 'state' 
    ? data.byState.map(state => {
        // Usar coordenadas da primeira cidade do estado
        const firstDDD = state.ddds[0];
        const cityData = data.byDDD.find(d => d.ddd === firstDDD);
        return {
          ...state,
          lat: cityData?.lat || 0,
          lng: cityData?.lng || 0,
          label: state.state,
          count: filter === 'active' ? state.activeCount : state.totalCount
        };
      })
    : data.byDDD.map(ddd => ({
        ...ddd,
        label: `${ddd.city} (${ddd.ddd})`,
        count: filter === 'active' ? ddd.activeCount : ddd.totalCount
      }));

  // Colunas da tabela de times
  const columns = [
    {
      title: 'Time',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name)
    },
    {
      title: 'Estado',
      dataIndex: 'state',
      key: 'state',
      filters: [...new Set(data.teams.map(t => t.state))].map(s => ({ text: s, value: s })),
      onFilter: (value, record) => record.state === value
    },
    {
      title: 'Cidade',
      dataIndex: 'city',
      key: 'city',
      sorter: (a, b) => a.city.localeCompare(b.city)
    },
    {
      title: 'DDD',
      dataIndex: 'ddd',
      key: 'ddd',
      sorter: (a, b) => a.ddd - b.ddd
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'ATIVO' : 'INATIVO'}
        </Tag>
      ),
      filters: [
        { text: 'ATIVO', value: true },
        { text: 'INATIVO', value: false }
      ],
      onFilter: (value, record) => record.isActive === value
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <h1>Mapa de Cobertura</h1>
      
      {/* Estatísticas */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={6}>
          <Card>
            <Statistic 
              title="Estados com Times" 
              value={data.summary.totalStates} 
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="Cidades com Times" 
              value={data.summary.totalCities} 
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="Times Ativos" 
              value={data.summary.activeTeams} 
            />
            {data.summary.teamsWithoutDDD > 0 && (
              <div style={{ fontSize: '12px', color: '#888', marginTop: '8px' }}>
                * {data.summary.teamsWithoutDDD} sem localização no mapa
              </div>
            )}
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="Total de Times" 
              value={data.summary.totalTeams} 
            />
          </Card>
        </Col>
      </Row>

      {/* Filtros */}
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={16}>
          <Col span={12}>
            <label style={{ marginRight: '8px' }}>Filtrar Times:</label>
            <Select 
              value={filter} 
              onChange={setFilter}
              style={{ width: 200 }}
            >
              <Option value="active">Apenas Ativos</Option>
              <Option value="all">Todos os Times</Option>
            </Select>
          </Col>
          <Col span={12}>
            <label style={{ marginRight: '8px' }}>Visualização:</label>
            <Select 
              value={viewMode} 
              onChange={setViewMode}
              style={{ width: 200 }}
            >
              <Option value="state">Por Estado</Option>
              <Option value="city">Por Cidade</Option>
            </Select>
          </Col>
        </Row>
      </Card>

      {/* Mapa */}
      <Card 
        style={{ 
          marginBottom: '24px',
          ...(isFullscreen && {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            margin: 0,
            borderRadius: 0,
            height: '100vh'
          })
        }}
        title={isFullscreen ? 'Mapa de Cobertura Futezap Brasil' : null}
        extra={
          <Button 
            icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
            onClick={toggleFullscreen}
          >
            {isFullscreen ? 'Sair do Fullscreen' : 'Fullscreen'}
          </Button>
        }
      >
        <MapContainer
          center={[-15.7801, -47.9292]} // Centro do Brasil
          zoom={4}
          maxBounds={[[-35, -75], [5, -30]]} // Limites do Brasil
          minZoom={4}
          style={{ height: isFullscreen ? 'calc(100vh - 60px)' : '600px', width: '100%' }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          />
          
          {markers.map((marker, index) => (
            <CircleMarker
              key={index}
              center={[marker.lat, marker.lng]}
              radius={Math.max(10, marker.count * 2)}
              fillColor={filter === 'active' ? '#52c41a' : '#1890ff'}
              color="#fff"
              weight={2}
              opacity={1}
              fillOpacity={0.6}
              eventHandlers={{
                click: () => {
                  if (viewMode === 'state') {
                    setSelectedState(marker.state);
                  } else {
                    setSelectedState(marker.state);
                  }
                }
              }}
            >
              <Popup>
                <div>
                  <h3>{marker.label}</h3>
                  {viewMode === 'state' ? (
                    <>
                      <p><strong>DDDs:</strong> {marker.ddds.join(', ')}</p>
                      <p><strong>Times Ativos:</strong> {marker.activeCount}</p>
                      <p><strong>Total de Times:</strong> {marker.totalCount}</p>
                    </>
                  ) : (
                    <>
                      <p><strong>Estado:</strong> {marker.state}</p>
                      <p><strong>Cidade:</strong> {marker.city}</p>
                      <p><strong>Times Ativos:</strong> {marker.activeCount}</p>
                      <p><strong>Total de Times:</strong> {marker.totalCount}</p>
                    </>
                  )}
                </div>
              </Popup>
            </CircleMarker>
          ))}
        </MapContainer>
      </Card>

      {/* Tabela de Times */}
      <Card 
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>
              {selectedState ? `Times do Estado: ${selectedState}` : 'Lista de Times'}
            </span>
            {selectedState && (
              <a onClick={() => setSelectedState(null)} style={{ fontSize: '14px' }}>
                Limpar filtro
              </a>
            )}
          </div>
        }
      >
        <Table
          columns={columns}
          dataSource={filteredTeams}
          rowKey="id"
          pagination={{ pageSize: 20 }}
        />
      </Card>
    </div>
  );
};

export default CoverageMap;
